// JARVIS Self-Brain — autonomous intelligence engine.
// JARVIS mines its own stored data, discovers patterns in patterns,
// builds its own trading strategies, proposes hypotheses, and tracks
// whether they prove true over time.
// No external API needed — this runs entirely on local data.

import { secureGet, secureSet } from '../utils/encryptionUtils';
import {
  getRules,
  getBehaviors,
  getTrades,
  getPredictions,
  getJarvisMemoryData,
  updateJarvisMemory,
} from '../services/storage';

// ─── Storage keys ─────────────────────────────────────────────────────
const SK = {
  STRATEGIES:  'jarvis_strategies',
  HYPOTHESES:  'jarvis_hypotheses',
};

// ─── Strategy status values ────────────────────────────────────────────
export const STRATEGY_STATUS = {
  HYPOTHESIS: 'HYPOTHESIS',   // Just created, untested
  TESTING:    'TESTING',      // Has some trades, not enough yet
  PROVEN:     'PROVEN',       // 10+ trades, ≥60% win rate
  STRONG:     'STRONG',       // 20+ trades, ≥70% win rate
  RETIRED:    'RETIRED',      // Disproven or outdated
};

// ─── Hypothesis confidence ─────────────────────────────────────────────
export const HYP_STATUS = {
  PROPOSED:   'PROPOSED',
  CONFIRMED:  'CONFIRMED',
  DISPROVEN:  'DISPROVEN',
  TESTING:    'TESTING',
};

// ═══════════════════════════════════════════════════════════════════════
// STRATEGY STORAGE
// ═══════════════════════════════════════════════════════════════════════
export const getStrategies = () => secureGet(SK.STRATEGIES, []);

export const saveStrategy = (strategy) => {
  const list = getStrategies();
  const id = strategy.id || `STR${String(list.length + 1).padStart(3, '0')}`;

  // Prevent duplicate titles
  if (!strategy.id && list.some(s => s.title.toLowerCase() === (strategy.title || '').toLowerCase())) {
    return list.find(s => s.title.toLowerCase() === strategy.title.toLowerCase());
  }

  const entry = {
    id,
    title:         strategy.title || 'Untitled Strategy',
    description:   strategy.description || '',
    // Conditions that must ALL be true to trigger this strategy
    conditions:    strategy.conditions || [],  // e.g. ['D1 bullish', 'H4 OB respected', 'London session']
    entry_logic:   strategy.entry_logic || '',
    sl_logic:      strategy.sl_logic || 'Below OB',
    tp_logic:      strategy.tp_logic || 'Next liquidity pool',
    session_filter: strategy.session_filter || 'Any',
    day_filter:    strategy.day_filter || 'Any',
    htf_bias:      strategy.htf_bias || 'Any',
    min_confluence: strategy.min_confluence || 6,  // minimum score out of 10
    // Rules that must be satisfied (IDs from Rules store)
    required_rules: strategy.required_rules || [],
    // Behaviors that support this strategy
    supporting_behaviors: strategy.supporting_behaviors || [],
    // Source
    source:        strategy.source || 'JARVIS Self-Generated',
    auto_generated: strategy.auto_generated !== false,
    // Performance tracking
    win_count:     0,
    loss_count:    0,
    total_trades:  0,
    win_rate:      0,
    avg_rr:        0,
    status:        STRATEGY_STATUS.HYPOTHESIS,
    // Timeline
    created_at:    new Date().toISOString(),
    last_updated:  new Date().toISOString(),
    last_trade_date: null,
    // Evolution
    version:       1,
    parent_id:     strategy.parent_id || null,
    evolution_notes: [],
  };

  secureSet(SK.STRATEGIES, [...list, entry]);
  return entry;
};

export const updateStrategyResult = (strategyId, won, rrAchieved = 0) => {
  const list = getStrategies();
  secureSet(SK.STRATEGIES, list.map(s => {
    if (s.id !== strategyId) return s;
    const wins  = s.win_count  + (won ? 1 : 0);
    const total = s.total_trades + 1;
    const losses = total - wins;
    const winRate = Math.round((wins / total) * 100);
    const avgRR   = ((s.avg_rr * s.total_trades) + rrAchieved) / total;

    // Auto-promote status
    let status = s.status;
    if (total >= 20 && winRate >= 70) status = STRATEGY_STATUS.STRONG;
    else if (total >= 10 && winRate >= 60) status = STRATEGY_STATUS.PROVEN;
    else if (total >= 3) status = STRATEGY_STATUS.TESTING;
    else if (total >= 1 && winRate < 35 && total >= 5) status = STRATEGY_STATUS.RETIRED;

    return {
      ...s,
      win_count:   wins,
      loss_count:  losses,
      total_trades: total,
      win_rate:    winRate,
      avg_rr:      Math.round(avgRR * 100) / 100,
      status,
      last_trade_date: new Date().toISOString(),
      last_updated:    new Date().toISOString(),
    };
  }));
};

export const retireStrategy = (strategyId, reason) => {
  const list = getStrategies();
  secureSet(SK.STRATEGIES, list.map(s =>
    s.id === strategyId
      ? { ...s, status: STRATEGY_STATUS.RETIRED, retirement_reason: reason, last_updated: new Date().toISOString() }
      : s
  ));
};

// ═══════════════════════════════════════════════════════════════════════
// HYPOTHESIS STORAGE
// ═══════════════════════════════════════════════════════════════════════
export const getHypotheses = () => secureGet(SK.HYPOTHESES, []);

export const saveHypothesis = (hyp) => {
  const list = getHypotheses();
  const entry = {
    id:          `HYP${String(list.length + 1).padStart(3, '0')}`,
    statement:   hyp.statement || '',
    // "IF [conditions], THEN [expected outcome]"
    if_conditions: hyp.if_conditions || [],
    then_outcome:  hyp.then_outcome || '',
    confidence:    HYP_STATUS.PROPOSED,
    jarvis_confidence_pct: hyp.jarvis_confidence_pct || 50,
    // Supporting evidence from existing data
    evidence_for:   hyp.evidence_for || [],
    evidence_against: hyp.evidence_against || [],
    // Testing
    trades_tested:  0,
    confirmed_count: 0,
    disproven_count: 0,
    created_at:  new Date().toISOString(),
    last_tested: null,
    notes:       hyp.notes || '',
    source:      hyp.source || 'JARVIS Self-Proposed',
  };
  secureSet(SK.HYPOTHESES, [...list, entry]);
  return entry;
};

export const testHypothesis = (hypId, confirmed) => {
  const list = getHypotheses();
  secureSet(SK.HYPOTHESES, list.map(h => {
    if (h.id !== hypId) return h;
    const confirmed_count = h.confirmed_count + (confirmed ? 1 : 0);
    const disproven_count = h.disproven_count + (confirmed ? 0 : 1);
    const total = h.trades_tested + 1;
    const rate  = confirmed_count / total;

    let confidence = HYP_STATUS.TESTING;
    if (total >= 5 && rate >= 0.7) confidence = HYP_STATUS.CONFIRMED;
    if (total >= 5 && rate <= 0.3) confidence = HYP_STATUS.DISPROVEN;

    return {
      ...h,
      trades_tested:   total,
      confirmed_count,
      disproven_count,
      confidence,
      last_tested: new Date().toISOString(),
    };
  }));
};

// ═══════════════════════════════════════════════════════════════════════
// PATTERN MINING — JARVIS analyzes its own data to find insights
// ═══════════════════════════════════════════════════════════════════════

// Which rule combinations produce the best win rates?
export const mineWinningRuleCombinations = () => {
  const trades  = getTrades().filter(t => t.result && t.result !== 'OPEN' && t.rules_used?.length >= 2);
  const combos  = {};

  trades.forEach(t => {
    const key = [...(t.rules_used || [])].sort().join('+');
    if (!combos[key]) combos[key] = { rules: t.rules_used, wins: 0, total: 0 };
    combos[key].total++;
    if (t.result === 'WIN') combos[key].wins++;
  });

  return Object.values(combos)
    .filter(c => c.total >= 2)
    .map(c => ({ ...c, winRate: Math.round((c.wins / c.total) * 100) }))
    .sort((a, b) => b.winRate - a.winRate || b.total - a.total);
};

// Which sessions produce the best results for each setup type?
export const mineSessionSetupMatrix = () => {
  const trades  = getTrades().filter(t => t.result && t.result !== 'OPEN');
  const matrix  = {};

  trades.forEach(t => {
    const session = t.session || 'Unknown';
    const setup   = t.setup_type || 'Unknown';
    const key     = `${session}::${setup}`;
    if (!matrix[key]) matrix[key] = { session, setup, wins: 0, total: 0 };
    matrix[key].total++;
    if (t.result === 'WIN') matrix[key].wins++;
  });

  return Object.values(matrix)
    .filter(m => m.total >= 2)
    .map(m => ({ ...m, winRate: Math.round((m.wins / m.total) * 100) }))
    .sort((a, b) => b.winRate - a.winRate);
};

// Which days of the week are most profitable?
export const mineDayOfWeekEdge = () => {
  const trades = getTrades().filter(t => t.result && t.result !== 'OPEN');
  const days   = {};

  trades.forEach(t => {
    const day = t.day_of_week || new Date(t.date).toLocaleDateString('en-IN', { weekday: 'long', timeZone: 'Asia/Kolkata' });
    if (!days[day]) days[day] = { day, wins: 0, total: 0, totalRR: 0 };
    days[day].total++;
    if (t.result === 'WIN') days[day].wins++;
    days[day].totalRR += parseFloat(t.rr_achieved || 0);
  });

  return Object.values(days)
    .filter(d => d.total >= 3)
    .map(d => ({
      ...d,
      winRate: Math.round((d.wins / d.total) * 100),
      avgRR:   Math.round((d.totalRR / d.total) * 100) / 100,
    }))
    .sort((a, b) => b.winRate - a.winRate);
};

// Psychology score vs win rate correlation
export const minePsychologyEdge = () => {
  const trades = getTrades().filter(t => t.result && t.result !== 'OPEN' && t.psychology_score);
  const buckets = { 'High (8–10)': { wins: 0, total: 0 }, 'Mid (5–7)': { wins: 0, total: 0 }, 'Low (1–4)': { wins: 0, total: 0 } };

  trades.forEach(t => {
    const score = parseInt(t.psychology_score || 0);
    const bucket = score >= 8 ? 'High (8–10)' : score >= 5 ? 'Mid (5–7)' : 'Low (1–4)';
    buckets[bucket].total++;
    if (t.result === 'WIN') buckets[bucket].wins++;
  });

  return Object.entries(buckets)
    .filter(([, v]) => v.total > 0)
    .map(([label, v]) => ({ label, ...v, winRate: Math.round((v.wins / v.total) * 100) }));
};

// ═══════════════════════════════════════════════════════════════════════
// AUTO-STRATEGY BUILDER — JARVIS proposes strategies from mined data
// ═══════════════════════════════════════════════════════════════════════

// Main entry point — call this after enough trade data accumulates.
// Returns newly generated strategies.
export const autoGenerateStrategies = () => {
  const trades = getTrades().filter(t => t.result && t.result !== 'OPEN');
  if (trades.length < 5) return [];  // Need at least 5 closed trades

  const rules       = getRules().filter(r => r.status === 'active');
  const behaviors   = getBehaviors().filter(b => ['HIGH', 'CONFIRMED'].includes(b.confidence));
  const existing    = getStrategies().map(s => s.title.toLowerCase());
  const generated   = [];

  // ── Strategy 1: Best session + best setup combo ──
  const matrix = mineSessionSetupMatrix();
  const top = matrix.find(m => m.winRate >= 65 && m.total >= 3);
  if (top) {
    const title = `${top.session} ${top.setup} Strategy`;
    if (!existing.includes(title.toLowerCase())) {
      const s = saveStrategy({
        title,
        description:   `Auto-discovered: ${top.setup} setups during ${top.session} session have ${top.winRate}% win rate over ${top.total} trades.`,
        conditions:    [`Session is ${top.session}`, `Setup type is ${top.setup}`],
        entry_logic:   `Wait for ${top.setup} confirmation during ${top.session} session`,
        session_filter: top.session,
        required_rules: rules.filter(r => r.session === top.session || r.session === 'Any').slice(0, 3).map(r => r.id),
        auto_generated: true,
        source:        'JARVIS Pattern Mining',
      });
      generated.push(s);
    }
  }

  // ── Strategy 2: Best day of week + high win-rate rules ──
  const dayEdge = mineDayOfWeekEdge();
  const bestDay = dayEdge.find(d => d.winRate >= 65 && d.total >= 3);
  const topRules = rules.filter(r => r.win_rate >= 65 && r.total_count >= 5);
  if (bestDay && topRules.length >= 2) {
    const title = `${bestDay.day} High-Probability Setup`;
    if (!existing.includes(title.toLowerCase())) {
      const s = saveStrategy({
        title,
        description:   `${bestDay.day} shows ${bestDay.winRate}% win rate. Combined with top-performing rules for maximum edge.`,
        conditions:    [`Day is ${bestDay.day}`, ...topRules.slice(0, 3).map(r => r.title)],
        entry_logic:   `On ${bestDay.day}, wait for confirmation of ${topRules[0]?.title || 'primary setup'}`,
        day_filter:    bestDay.day,
        required_rules: topRules.slice(0, 3).map(r => r.id),
        auto_generated: true,
        source:        'JARVIS Day-of-Week Mining',
      });
      generated.push(s);
    }
  }

  // ── Strategy 3: Behavior-driven strategy from confirmed behaviors ──
  const confirmedBehaviors = behaviors.filter(b => b.confidence === 'CONFIRMED');
  if (confirmedBehaviors.length >= 2) {
    const title = 'Confirmed Behavior Confluence Strategy';
    if (!existing.includes(title.toLowerCase())) {
      const s = saveStrategy({
        title,
        description:   `Combines ${confirmedBehaviors.length} CONFIRMED behaviors for maximum probability entry.`,
        conditions:    confirmedBehaviors.slice(0, 4).map(b => b.title),
        entry_logic:   'Wait until at least 3 confirmed behaviors align simultaneously',
        min_confluence: 7,
        supporting_behaviors: confirmedBehaviors.slice(0, 4).map(b => b.title),
        auto_generated: true,
        source:        'JARVIS Behavior Synthesis',
      });
      generated.push(s);
    }
  }

  // Update JARVIS memory with strategy count
  const memory = getJarvisMemoryData();
  updateJarvisMemory({
    last_strategy_generation: new Date().toISOString(),
    self_generated_strategies: (memory.self_generated_strategies || 0) + generated.length,
  });

  return generated;
};

// ═══════════════════════════════════════════════════════════════════════
// HYPOTHESIS ENGINE — JARVIS proposes IF-THEN hypotheses to test
// ═══════════════════════════════════════════════════════════════════════

export const autoProposedHypotheses = () => {
  const trades      = getTrades().filter(t => t.result && t.result !== 'OPEN');
  const predictions = getPredictions();
  const existing    = getHypotheses().map(h => h.statement.toLowerCase());
  const proposed    = [];

  if (trades.length < 5) return [];

  // ── Hypothesis 1: Psychology edge ──
  const psychEdge = minePsychologyEdge();
  const highPsych = psychEdge.find(p => p.label === 'High (8–10)');
  const lowPsych  = psychEdge.find(p => p.label === 'Low (1–4)');
  if (highPsych?.total >= 3 && lowPsych?.total >= 3) {
    const stmt = 'High psychology score (8+) correlates with higher win rate';
    if (!existing.includes(stmt.toLowerCase())) {
      const h = saveHypothesis({
        statement:     stmt,
        if_conditions: ['Psychology score is 8 or above before trade'],
        then_outcome:  'Win rate is significantly higher than low-psychology sessions',
        jarvis_confidence_pct: highPsych.winRate > lowPsych.winRate ? 75 : 40,
        evidence_for:  highPsych.total >= 3 ? [`High psych: ${highPsych.winRate}% WR over ${highPsych.total} trades`] : [],
        evidence_against: lowPsych.winRate > highPsych.winRate ? [`Low psych actually wins ${lowPsych.winRate}%`] : [],
        source:        'JARVIS Psychology Mining',
      });
      proposed.push(h);
    }
  }

  // ── Hypothesis 2: Session time edge ──
  const matrix = mineSessionSetupMatrix();
  const dominantCombo = matrix[0];
  if (dominantCombo?.winRate >= 65 && dominantCombo?.total >= 4) {
    const stmt = `${dominantCombo.setup} setups during ${dominantCombo.session} session are high-probability`;
    if (!existing.includes(stmt.toLowerCase())) {
      const h = saveHypothesis({
        statement:     stmt,
        if_conditions: [`Session is ${dominantCombo.session}`, `Setup type is ${dominantCombo.setup}`],
        then_outcome:  `Trade wins at ${dominantCombo.winRate}% rate`,
        jarvis_confidence_pct: dominantCombo.winRate,
        evidence_for:  [`${dominantCombo.winRate}% WR over ${dominantCombo.total} trades`],
        source:        'JARVIS Session Mining',
      });
      proposed.push(h);
    }
  }

  // ── Hypothesis 3: Prediction accuracy direction bias ──
  const bullPreds = predictions.filter(p => p.direction === 'BUY'  && p.outcome_recorded);
  const bearPreds = predictions.filter(p => p.direction === 'SELL' && p.outcome_recorded);
  if (bullPreds.length >= 5 && bearPreds.length >= 5) {
    const bullAcc = bullPreds.filter(p => p.was_correct === 'CORRECT').length / bullPreds.length;
    const bearAcc = bearPreds.filter(p => p.was_correct === 'CORRECT').length / bearPreds.length;
    const biasDir = bullAcc > bearAcc ? 'BUY' : 'SELL';
    const stmt    = `JARVIS is more accurate on ${biasDir} setups than the opposite direction`;
    if (!existing.includes(stmt.toLowerCase()) && Math.abs(bullAcc - bearAcc) > 0.15) {
      const h = saveHypothesis({
        statement:     stmt,
        if_conditions: [`Setup direction is ${biasDir}`],
        then_outcome:  'Higher prediction accuracy and win rate',
        jarvis_confidence_pct: Math.round(Math.abs(bullAcc - bearAcc) * 100) + 50,
        evidence_for:  [`${biasDir} accuracy: ${Math.round((biasDir === 'BUY' ? bullAcc : bearAcc) * 100)}%`],
        source:        'JARVIS Prediction Analysis',
      });
      proposed.push(h);
    }
  }

  return proposed;
};

// ═══════════════════════════════════════════════════════════════════════
// SELF-ASSESSMENT — JARVIS knows where it is good and bad
// ═══════════════════════════════════════════════════════════════════════

export const getSelfStrengths = () => {
  const strategies = getStrategies().filter(s => [STRATEGY_STATUS.PROVEN, STRATEGY_STATUS.STRONG].includes(s.status));
  const sessionMatrix = mineSessionSetupMatrix().slice(0, 3);
  const topRules = getRules().filter(r => r.win_rate >= 65 && r.total_count >= 5);

  return {
    provenStrategies: strategies,
    bestSessionSetups: sessionMatrix,
    highConfidenceRules: topRules,
    summary: strategies.length > 0
      ? `${strategies.length} proven strategies, best: ${strategies[0]?.title}`
      : 'Still collecting data to identify strengths',
  };
};

export const getSelfWeaknesses = () => {
  const rules    = getRules().filter(r => r.confidence === 'under_review' || (r.win_rate < 45 && r.total_count >= 5));
  const retired  = getStrategies().filter(s => s.status === STRATEGY_STATUS.RETIRED);
  const disproved = getHypotheses().filter(h => h.confidence === HYP_STATUS.DISPROVEN);

  return {
    underperformingRules: rules,
    retiredStrategies: retired,
    disprovedHypotheses: disproved,
    summary: rules.length > 0
      ? `${rules.length} underperforming rules need review`
      : 'No major weaknesses identified yet',
  };
};

// ═══════════════════════════════════════════════════════════════════════
// FULL BRAIN REPORT — used in system prompt and UI
// ═══════════════════════════════════════════════════════════════════════

export const getBrainReport = () => {
  const strategies  = getStrategies();
  const hypotheses  = getHypotheses();
  const strengths   = getSelfStrengths();
  const weaknesses  = getSelfWeaknesses();

  const activeStrats  = strategies.filter(s => s.status !== STRATEGY_STATUS.RETIRED);
  const provenStrats  = strategies.filter(s => [STRATEGY_STATUS.PROVEN, STRATEGY_STATUS.STRONG].includes(s.status));
  const confirmedHyps = hypotheses.filter(h => h.confidence === HYP_STATUS.CONFIRMED);

  return {
    strategies: {
      total:   strategies.length,
      active:  activeStrats.length,
      proven:  provenStrats.length,
      list:    activeStrats,
    },
    hypotheses: {
      total:     hypotheses.length,
      confirmed: confirmedHyps.length,
      testing:   hypotheses.filter(h => h.confidence === HYP_STATUS.TESTING).length,
      list:      hypotheses,
    },
    strengths,
    weaknesses,
    miningInsights: {
      sessionMatrix: mineSessionSetupMatrix().slice(0, 5),
      dayEdge:       mineDayOfWeekEdge().slice(0, 5),
      psychEdge:     minePsychologyEdge(),
    },
  };
};

// ═══════════════════════════════════════════════════════════════════════
// STRATEGY EVOLUTION — prune weak, evolve strong
// Called periodically (weekly) to keep strategies current.
// ═══════════════════════════════════════════════════════════════════════

export const evolveStrategies = () => {
  const strategies = getStrategies();
  let retired = 0;
  let evolved  = 0;

  strategies.forEach(s => {
    // Retire strategies with bad performance after enough trades
    if (s.total_trades >= 8 && s.win_rate < 40 && s.status !== STRATEGY_STATUS.RETIRED) {
      retireStrategy(s.id, `Win rate fell below 40% after ${s.total_trades} trades`);
      retired++;
    }
    // Upgrade TESTING → PROVEN
    if (s.status === STRATEGY_STATUS.TESTING && s.total_trades >= 10 && s.win_rate >= 60) {
      const list = getStrategies();
      secureSet(SK.STRATEGIES, list.map(st =>
        st.id === s.id ? { ...st, status: STRATEGY_STATUS.PROVEN, last_updated: new Date().toISOString() } : st
      ));
      evolved++;
    }
  });

  return { retired, evolved };
};
